import { User, Phone, ChevronsUpDown, Check } from "lucide-react";
import { useState, useEffect, useRef } from "react";

import { leadsService } from "@/app/(authenticated)/leads/_modules/services/services";
import type { Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/app/_module/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import { cn } from "@/app/_module/lib/utils";
import { formatPhoneNumber } from "@/app/_module/utils/formatters";

interface LeadSelectProps {
  value?: string;
  onValueChange: (leadId: string, leadName?: string, leadState?: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const LeadSelect = function ({
  value,
  onValueChange,
  placeholder = "Select a lead...",
  disabled = false,
}: LeadSelectProps) {
  const [open, setOpen] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [selectedLeadData, setSelectedLeadData] = useState<Lead | null>(null);

  // Fetch the selected lead by ID when a value is provided (for editing)
  useEffect(() => {
    const fetchSelectedLead = async () => {
      if (!value) {
        setSelectedLeadData(null);
        return;
      }

      // Check if the lead is already in the loaded list
      const existingLead = leads.find((lead) => lead.id === value);
      if (existingLead) {
        setSelectedLeadData(existingLead);
        return;
      }

      // Fetch the specific lead by ID
      try {
        const response = await leadsService.getLead(value);
        // Unwrap the response - API returns { statusCode, message, data, timestamp }
        // The lead data may be in response.data or response.data.data
        const unwrapLead = (res: unknown): Lead | null => {
          if (!res) return null;
          const r = res as Record<string, unknown>;
          // Check for direct lead properties (id, name)
          if (r.id && typeof r.id === 'string') return r as unknown as Lead;
          // Check for wrapped response
          if (r.data) {
            const d = r.data as Record<string, unknown>;
            if (d.id && typeof d.id === 'string') return d as unknown as Lead;
            // Double-wrapped case
            if (d.data) {
              const dd = d.data as Record<string, unknown>;
              if (dd.id && typeof dd.id === 'string') return dd as unknown as Lead;
            }
          }
          return null;
        };
        const leadData = unwrapLead(response);
        if (leadData) {
          setSelectedLeadData(leadData);
        }
      } catch {
        // If we can't fetch the lead, clear selected data
        setSelectedLeadData(null);
      }
    };

    void fetchSelectedLead();
  }, [value, leads]);

  // Debounce search query to prevent excessive API calls
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // Fetch leads when debounced query changes
  useEffect(() => {
    const fetchLeads = async () => {
      setLoading(true);
      try {
        // Use myLeadsOnly=true to only fetch leads owned by or assigned to the current user
        // This prevents showing leads belonging to other users in the agency
        const response = await leadsService.getLeads({
          page: 1,
          limit: 200,
          myLeadsOnly: 'true',
          ...(debouncedQuery && { query: debouncedQuery }),
        });

        // Handle different response structures safely
        const unwrap = (input: unknown): Lead[] => {
          if (Array.isArray(input)) return input as Lead[];
          if (input && typeof input === "object") {
            const obj = input as Record<string, unknown>;
            if (Array.isArray(obj.items)) return obj.items as Lead[];
            if (obj.data && typeof obj.data === "object") {
              const d = obj.data as Record<string, unknown>;
              if (Array.isArray(d.items)) return d.items as Lead[];
              if (Array.isArray(d)) return d as unknown as Lead[];
            }
          }
          return [];
        };
        const leadsData: Lead[] = unwrap(response as unknown);

        setLeads(leadsData);
      } catch (error) {
        setLeads([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchLeads();
  }, [debouncedQuery]);

  // Use selectedLeadData (fetched by ID) first, then fall back to searching in loaded leads
  const selectedLead = selectedLeadData || leads.find((lead) => lead.id === value);

  const handleSelect = (leadId: string) => {
    const lead = leads.find((l) => l.id === leadId);
    const leadName = lead?.name || `Lead ${leadId}`;
    const leadState = lead?.state;
    onValueChange(leadId, leadName, leadState);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          {selectedLead ? (
            <div className="flex items-center gap-2 truncate">
              <User className="h-4 w-4 shrink-0" />
              <span className="truncate">{selectedLead.name || "Unnamed Lead"}</span>
              {!!selectedLead.leadStat && (
                <Badge variant="secondary" className="ml-1">
                  {selectedLead.leadStat}
                </Badge>
              )}
            </div>
          ) : (
            <span className="text-muted-foreground">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[400px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search leads by name, email, or phone..."
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            {loading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                Loading leads...
              </div>
            ) : leads.length === 0 ? (
              <CommandEmpty>No leads found.</CommandEmpty>
            ) : (
              <CommandGroup>
                {leads.map((lead) => (
                  <CommandItem
                    key={lead.id}
                    value={lead.id}
                    onSelect={handleSelect}
                    className="flex flex-col items-start gap-1 py-3"
                  >
                    <div className="flex items-center gap-2 w-full">
                      <Check
                        className={cn(
                          "h-4 w-4 shrink-0",
                          value === lead.id ? "opacity-100" : "opacity-0"
                        )}
                      />
                      <User className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium">{lead.name || "Unnamed Lead"}</span>
                      {!!lead.leadStat && (
                        <Badge variant="secondary" className="ml-auto">
                          {lead.leadStat}
                        </Badge>
                      )}
                    </div>
                    {/* Phone only — emails cluttered the row and overflowed the popover */}
                    {!!lead.phone && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground ml-10">
                        <Phone className="h-3 w-3" />
                        <span>{formatPhoneNumber(lead.phone)}</span>
                      </div>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
